import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { getLocations } from '@app/adapter/catalog-service';
import { locationsByPrefectureSelector } from '@app/domain/catalog';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { ProductLocation, ProductLocationType } from '@app/types/catalog';
import { isError } from '@app/utils/error';

export const useLocation = () => {
  const setSnackbar = useSetSnackbar();
  const prefectures = useRecoilValue(locationsByPrefectureSelector);
  const [cities, setCities] = useState<ProductLocation[]>([]);
  const [prefectureId, setPrefectureId] = useState<string>();
  const [isCityLoading, setCityLoading] = useState(false);

  const fetchLocationCities = useCallback(
    async (prefecture: string): Promise<ProductLocation[]> => {
      setCities([]);
      const prefectureId =
        prefectures.find((p) => p.name === prefecture)?.id || '';
      if (!prefectureId) return [];

      setCityLoading(true);
      try {
        const { data } = await getLocations({
          parentId: prefectureId,
          type: ProductLocationType.CITY,
        });
        if (data['@nextLink']) {
          const { data: addData } = await getLocations({
            '@nextLink': data['@nextLink'],
          });
          const values = [...data.value, ...addData.value];
          setCities(values);
          return values;
        }
        setCities(data.value);
        return data.value;
      } catch (error) {
        setSnackbar(true, '市区町村の取得に失敗しました', 'error');
        if (isError(error)) {
          console.error(error.message);
        }
        return [];
      } finally {
        setCityLoading(false);
      }
    },
    [prefectures, setSnackbar]
  );

  useEffect(() => {
    const prefecture =
      prefectures.find((p) => p.id === prefectureId)?.name || '';
    void fetchLocationCities(prefecture);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefectureId]);

  return {
    cities,
    fetchLocationCities,
    isCityLoading,
    prefectureId,
    prefectures,
    setPrefectureId,
  };
};
